import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import * as moment from 'moment';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IoService } from '../../../services/io/io.service';
import { ReportService } from '../../../services/report/report.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { LocationService } from '../../../services/location/location.service';
import { ListBuilderService } from '../../../services/list-builder/list-builder.service';
import { DateRangeSettings } from '../filter-date-range/date-config-interface';
import { AppComponent } from '../../../app.component';

const BULK_CONFIRM_THRESHOLD = 20;

@Component({
  selector: 'app-detail-view-list',
  templateUrl: './detail-view-list.component.html',
  styleUrls: ['./detail-view-list.component.scss']
})
export class DetailViewListComponent implements OnInit, OnDestroy {

  viewKey: string;
  listKey: string;

  view: any;
  listName: string;
  memberCount: number;

  editValues: any = {};
  displayValues: { [path: string]: any } = {};
  arrayInputValues: { [path: string]: string } = {};
  dateTimeSettings: { [path: string]: DateRangeSettings } = {};

  variousSet: Set<string> = new Set();
  dirtyPaths: Set<string> = new Set();

  // Various array sourceCollection fields (Groups, Taxes, Locations…): every
  // distinct value across the list with its member count, from getBulkView.
  // Edits on these are add-to-all / remove-from-all deltas rather than a
  // whole-array replace, unless the user opts into replaceMode for the field.
  arrayValueCounts: { [path: string]: { value: string, display: string, count: number }[] } = {};
  arrayDeltas: { [path: string]: { add: { value: string, display: string }[], remove: string[] } } = {};
  replaceMode: { [path: string]: boolean } = {};

  errors: string[] = [];
  saving = false;
  canUpdate = false;
  // Bulk delete: offered only when the view supports it (getBulkView's
  // `deletable`) AND the user holds view.<viewKey>.delete.
  deleting = false;
  canDelete = false;
  deletable = false;
  activeLocation: any;

  rtState: { [path: string]: any } = {};
  rtDropdown: { [path: string]: string } = {};
  rtLinkInput: { [path: string]: { active: boolean, url: string } } = {};
  rtFontSizes = [
    { label: 'Small', value: '2' },
    { label: 'Normal', value: '3' },
    { label: 'Large', value: '5' },
    { label: 'Huge', value: '7' },
  ];
  rtColors = [
    '#000000', '#434343', '#e53935', '#d81b60',
    '#1e88e5', '#1565c0', '#43a047', '#2e7d32',
    '#fb8c00', '#f4511e', '#8e24aa', '#5e35b1',
  ];

  states = [
    { name: 'Alabama', key: 'AL' }, { name: 'Alaska', key: 'AK' }, { name: 'Arizona', key: 'AZ' },
    { name: 'Arkansas', key: 'AR' }, { name: 'California', key: 'CA' }, { name: 'Colorado', key: 'CO' },
    { name: 'Connecticut', key: 'CT' }, { name: 'Delaware', key: 'DE' }, { name: 'Florida', key: 'FL' },
    { name: 'Georgia', key: 'GA' }, { name: 'Hawaii', key: 'HI' }, { name: 'Idaho', key: 'ID' },
    { name: 'Illinois', key: 'IL' }, { name: 'Indiana', key: 'IN' }, { name: 'Iowa', key: 'IA' },
    { name: 'Kansas', key: 'KS' }, { name: 'Kentucky', key: 'KY' }, { name: 'Louisiana', key: 'LA' },
    { name: 'Maine', key: 'ME' }, { name: 'Maryland', key: 'MD' }, { name: 'Massachusetts', key: 'MA' },
    { name: 'Michigan', key: 'MI' }, { name: 'Minnesota', key: 'MN' }, { name: 'Mississippi', key: 'MS' },
    { name: 'Missouri', key: 'MO' }, { name: 'Montana', key: 'MT' }, { name: 'Nebraska', key: 'NE' },
    { name: 'Nevada', key: 'NV' }, { name: 'New Hampshire', key: 'NH' }, { name: 'New Jersey', key: 'NJ' },
    { name: 'New Mexico', key: 'NM' }, { name: 'New York', key: 'NY' }, { name: 'North Carolina', key: 'NC' },
    { name: 'North Dakota', key: 'ND' }, { name: 'Ohio', key: 'OH' }, { name: 'Oklahoma', key: 'OK' },
    { name: 'Oregon', key: 'OR' }, { name: 'Pennsylvania', key: 'PA' }, { name: 'Rhode Island', key: 'RI' },
    { name: 'South Carolina', key: 'SC' }, { name: 'South Dakota', key: 'SD' }, { name: 'Tennessee', key: 'TN' },
    { name: 'Texas', key: 'TX' }, { name: 'Utah', key: 'UT' }, { name: 'Vermont', key: 'VT' },
    { name: 'Virginia', key: 'VA' }, { name: 'Washington', key: 'WA' }, { name: 'West Virginia', key: 'WV' },
    { name: 'Wisconsin', key: 'WI' }, { name: 'Wyoming', key: 'WY' },
  ];

  private permissionsSub: Subscription;
  private locationSub: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ioService: IoService,
    private reportService: ReportService,
    private authorizationService: AuthorizationService,
    private locationService: LocationService,
    private listBuilderService: ListBuilderService,
    private sanitizer: DomSanitizer,
  ) { }

  ngOnInit(): void {
    this.activeLocation = this.locationService.getActiveLocation();

    this.route.paramMap.pipe(
      switchMap(params => {
        this.viewKey = params.get('viewKey');
        this.listKey = params.get('listKey');
        return of({ viewKey: this.viewKey, listKey: this.listKey });
      })
    ).subscribe(params => {
      if (params.viewKey && params.listKey) {
        this.view = null;
        this.errors = [];
        this.initView();
      }
    });

    this.locationSub = this.locationService.activeLocationSetObservable().subscribe(location => {
      this.activeLocation = location;
      if (this.view && this.view.fields.some(f => f.locationSpecific)) {
        this.loadView();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.permissionsSub) this.permissionsSub.unsubscribe();
    if (this.locationSub) this.locationSub.unsubscribe();
  }

  close() {
    this.reportService.closeDetailPanel(this.route, 3);
  }

  private initView() {
    if (this.permissionsSub) this.permissionsSub.unsubscribe();

    if (this.authorizationService.permissionsAreLoaded()) {
      this.canUpdate = this.authorizationService.checkPermission('view.' + this.viewKey + '.update', 'any', false);
      this.canDelete = this.authorizationService.checkPermission('view.' + this.viewKey + '.delete', 'any', false);
      this.loadView();
    } else {
      this.permissionsSub = this.authorizationService.permissionsLoadedObservable().subscribe(loaded => {
        if (loaded) {
          this.canUpdate = this.authorizationService.checkPermission('view.' + this.viewKey + '.update', 'any', false);
          this.canDelete = this.authorizationService.checkPermission('view.' + this.viewKey + '.delete', 'any', false);
          this.loadView();
          this.permissionsSub.unsubscribe();
        }
      });
    }
  }

  async loadView() {
    try {
      const result: any = await this.ioService.post('/view/getBulkView', {
        viewKey: this.viewKey,
        listKey: this.listKey,
        locationId: this.activeLocation?._id || null,
      });

      this.view = result.view;
      this.listName = result.listName;
      this.memberCount = result.memberCount || 0;
      this.deletable = !!result.deletable;
      this.variousSet = new Set(result.variousPaths || []);
      this.dirtyPaths = new Set();
      this.arrayValueCounts = result.arrayValueCounts || {};
      this.arrayDeltas = {};
      this.replaceMode = {};

      const commonValues = result.commonValues || {};
      const resolvedDisplayValues = result.displayValues || {};

      this.editValues = {};
      this.displayValues = {};
      this.dateTimeSettings = {};
      this.arrayInputValues = {};

      for (const field of this.view.fields) {
        if (field.calculated === true) continue;
        if (!field.editable) continue;

        const isVarious = this.variousSet.has(field.path);
        let value = isVarious ? null : commonValues[field.path];

        if ((field.type === 'currency' || field.type === 'advancedPrice') && value != null) {
          value = (value / 100).toFixed(2);
        } else if (field.type === 'dateTime') {
          const m = (!isVarious && value != null) ? moment(value) : moment();
          const settings = new DateRangeSettings();
          settings.dateConfig.singleDate = true;
          settings.dateConfig.includeTime = true;
          settings.dateConfig.autoApply = false;
          settings.dateConfig.showRanges = false;
          settings.dateConfig.ranges = null;
          settings.dateConfig.opens = 'left';
          settings.dateRange.startDate = m;
          settings.dateRange.endDate = m;
          this.dateTimeSettings[field.path] = settings;
        } else if (field.type === 'sourceCollection') {
          if (field.limit) {
            this.displayValues[field.path] = (!isVarious && resolvedDisplayValues[field.path]) ? resolvedDisplayValues[field.path] : [];
          } else {
            this.displayValues[field.path] = (!isVarious && resolvedDisplayValues[field.path]) ? resolvedDisplayValues[field.path] : '';
          }
        }

        if (field.type === 'address') {
          if (field.limit) {
            value = Array.isArray(value) ? value : [];
          } else {
            value = (value && !isVarious) ? value : {};
          }
        }

        if (field.limit && !Array.isArray(value)) {
          value = (value != null && !isVarious) ? [value] : [];
        }

        if (field.type === 'boolean' && isVarious) {
          value = null;
        }

        this.editValues[field.path] = value;
      }

      setTimeout(() => this.initRichTextEditors(), 0);
    } catch (e) {
      this.errors = [e?.error?.msg || 'Failed to load bulk view.'];
    }
  }

  isFieldEditable(field: any): boolean {
    if (!this.canUpdate || !field.editable) return false;
    if (field.calculated === true) return false;
    if (field.locationSpecific && (!this.activeLocation || !this.activeLocation._id)) return false;
    return true;
  }

  isFieldVisible(field: any): boolean {
    if (field.calculated === true) return false;
    if (!field.editable) return false;
    return true;
  }

  isArrayField(field: any): boolean {
    return field.limit != null && field.limit > 0;
  }

  isVarious(field: any): boolean {
    return this.variousSet.has(field.path) && !this.dirtyPaths.has(field.path);
  }

  markDirty(field: any) {
    this.dirtyPaths.add(field.path);
  }

  revertField(field: any) {
    this.dirtyPaths.delete(field.path);
    this.arrayDeltas[field.path] = { add: [], remove: [] };
    // Reset to "empty" state for various fields; reload would restore common values for non-various
    if (this.variousSet.has(field.path)) {
      if (field.type === 'address' && !field.limit) {
        this.editValues[field.path] = {};
      } else if (field.limit) {
        this.editValues[field.path] = [];
        if (field.type === 'sourceCollection') this.displayValues[field.path] = [];
      } else if (field.type === 'boolean') {
        this.editValues[field.path] = null;
      } else {
        this.editValues[field.path] = null;
        if (field.type === 'sourceCollection') this.displayValues[field.path] = '';
      }
      // Rich text: clear editor contents
      if (field.type === 'richText') {
        setTimeout(() => {
          const el = document.querySelector(`.rt-editor[data-path="${field.path}"]`) as HTMLElement;
          if (el) el.innerHTML = '';
        }, 0);
      }
    }
  }

  // ─── Booleans ───────────────────────────────────────────────

  setBoolean(field: any, value: boolean) {
    this.editValues[field.path] = value;
    this.markDirty(field);
  }

  toggleBoolean(field: any) {
    this.editValues[field.path] = !this.editValues[field.path];
    this.markDirty(field);
  }

  // ─── Source collection ──────────────────────────────────────

  onSourceSelectionChange(field: any, event: { value: string, display: string }) {
    this.editValues[field.path] = event.value;
    this.displayValues[field.path] = event.display;
    this.markDirty(field);
  }

  addSourceValue(field: any, event: { value: string, display: string }) {
    const arr: string[] = this.editValues[field.path] || [];
    const displayArr: any[] = this.displayValues[field.path] || [];
    if (arr.includes(event.value)) return;
    if (arr.length >= field.limit) return;
    arr.push(event.value);
    displayArr.push({ value: event.value, display: event.display });
    this.editValues[field.path] = arr;
    this.displayValues[field.path] = displayArr;
    this.markDirty(field);
  }

  removeSourceValue(field: any, index: number) {
    const arr: any[] = this.editValues[field.path];
    const displayArr: any[] = this.displayValues[field.path];
    if (arr) arr.splice(index, 1);
    if (displayArr) displayArr.splice(index, 1);
    this.markDirty(field);
  }

  // ─── Array sourceCollection deltas ──────────────────────────

  isDeltaField(field: any): boolean {
    return this.isArrayField(field) && field.type === 'sourceCollection'
      && this.variousSet.has(field.path) && !this.replaceMode[field.path];
  }

  private delta(path: string) {
    if (!this.arrayDeltas[path]) this.arrayDeltas[path] = { add: [], remove: [] };
    return this.arrayDeltas[path];
  }

  private syncDeltaDirty(field: any) {
    const d = this.delta(field.path);
    if (d.add.length || d.remove.length) this.dirtyPaths.add(field.path);
    else this.dirtyPaths.delete(field.path);
  }

  isQueuedRemove(field: any, value: string): boolean {
    return this.delta(field.path).remove.includes(value);
  }

  isQueuedAdd(field: any, value: string): boolean {
    return this.delta(field.path).add.some(a => a.value === value);
  }

  chipState(field: any, entry: { value: string, count: number }): 'removed' | 'added' | 'partial' | 'full' {
    if (this.isQueuedRemove(field, entry.value)) return 'removed';
    if (this.isQueuedAdd(field, entry.value)) return 'added';
    return entry.count < this.memberCount ? 'partial' : 'full';
  }

  queueRemove(field: any, value: string) {
    const d = this.delta(field.path);
    d.add = d.add.filter(a => a.value !== value);
    if (!d.remove.includes(value)) d.remove.push(value);
    this.syncDeltaDirty(field);
  }

  undoRemove(field: any, value: string) {
    const d = this.delta(field.path);
    d.remove = d.remove.filter(v => v !== value);
    this.syncDeltaDirty(field);
  }

  queueAdd(field: any, entry: { value: string, display: string }) {
    if (!entry || !entry.value) return;
    const d = this.delta(field.path);
    d.remove = d.remove.filter(v => v !== entry.value);
    if (!d.add.some(a => a.value === entry.value)) d.add.push({ value: entry.value, display: entry.display });
    this.syncDeltaDirty(field);
  }

  undoAdd(field: any, value: string) {
    const d = this.delta(field.path);
    d.add = d.add.filter(a => a.value !== value);
    this.syncDeltaDirty(field);
  }

  // Queued additions that aren't already present on any member (shown as new chips)
  newDeltaValues(field: any): { value: string, display: string }[] {
    const present = new Set((this.arrayValueCounts[field.path] || []).map(e => e.value));
    return this.delta(field.path).add.filter(a => !present.has(a.value));
  }

  deltaExcludeValues(field: any): string[] {
    const present = (this.arrayValueCounts[field.path] || []).map(e => e.value);
    return present.concat(this.delta(field.path).add.map(a => a.value));
  }

  setReplaceMode(field: any, on: boolean) {
    this.replaceMode[field.path] = on;
    this.arrayDeltas[field.path] = { add: [], remove: [] };
    this.editValues[field.path] = [];
    this.displayValues[field.path] = [];
    this.dirtyPaths.delete(field.path);
  }

  // ─── String arrays ──────────────────────────────────────────

  addStringValue(field: any, value: string) {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    const arr: string[] = this.editValues[field.path] || [];
    if (arr.includes(trimmed)) return;
    if (arr.length >= field.limit) return;
    arr.push(trimmed);
    this.editValues[field.path] = arr;
    this.markDirty(field);
  }

  removeArrayValue(field: any, index: number) {
    const arr: any[] = this.editValues[field.path];
    if (arr) arr.splice(index, 1);
    this.markDirty(field);
  }

  onArrayStringKeydown(event: KeyboardEvent, field: any) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = this.arrayInputValues[field.path] || '';
      this.addStringValue(field, value);
      this.arrayInputValues[field.path] = '';
    }
  }

  // ─── Addresses ──────────────────────────────────────────────

  addAddress(field: any) {
    const arr: any[] = this.editValues[field.path] || [];
    if (field.limit && arr.length >= field.limit) return;
    arr.push({});
    this.editValues[field.path] = arr;
    this.markDirty(field);
  }

  removeAddress(field: any, index: number) {
    const arr: any[] = this.editValues[field.path];
    if (arr) arr.splice(index, 1);
    this.markDirty(field);
  }

  formatAddress(addr: any): string {
    if (!addr) return '';
    const parts = [addr.streetAddress, addr.streetAddressExt].filter(p => p);
    const cityLine = [addr.addressLocality, addr.addressRegion].filter(p => p).join(', ');
    if (cityLine) parts.push(cityLine + (addr.postalCode ? ' ' + addr.postalCode : ''));
    else if (addr.postalCode) parts.push(addr.postalCode);
    return parts.join(', ');
  }

  // ─── Rich text ──────────────────────────────────────────────

  private initRichTextEditors() {
    const editors = document.querySelectorAll('.rt-editor[data-path]');
    editors.forEach((el: Element) => {
      const htmlEl = el as HTMLElement;
      const path = htmlEl.getAttribute('data-path');
      if (path && this.editValues[path]) {
        htmlEl.innerHTML = this.editValues[path];
      }
    });
  }

  onRtInput(event: Event, field: any) {
    this.editValues[field.path] = (event.target as HTMLElement).innerHTML;
    this.markDirty(field);
  }

  onRtPaste(event: ClipboardEvent, field: any) {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain');
    if (text) {
      document.execCommand('insertText', false, text);
    }
    const el = event.target as HTMLElement;
    this.editValues[field.path] = el.innerHTML;
    this.markDirty(field);
    this.updateRtState(field);
  }

  rtExec(event: Event, field: any, cmd: string) {
    event.preventDefault();
    const el = document.querySelector(`.rt-editor[data-path="${field.path}"]`) as HTMLElement;
    if (el) el.focus();
    document.execCommand(cmd, false);
    this.editValues[field.path] = el ? el.innerHTML : this.editValues[field.path];
    this.markDirty(field);
    this.updateRtState(field);
  }

  updateRtState(field: any) {
    this.rtState[field.path] = {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
    };
  }

  toggleRtDropdown(event: Event, field: any, name: string) {
    event.preventDefault();
    this.rtDropdown[field.path] = this.rtDropdown[field.path] === name ? '' : name;
  }

  rtSetFontSize(event: Event, field: any, size: string) {
    event.preventDefault();
    const el = document.querySelector(`.rt-editor[data-path="${field.path}"]`) as HTMLElement;
    if (el) el.focus();
    document.execCommand('fontSize', false, size);
    this.editValues[field.path] = el ? el.innerHTML : this.editValues[field.path];
    this.markDirty(field);
    this.rtDropdown[field.path] = '';
  }

  rtSetColor(event: Event, field: any, color: string) {
    event.preventDefault();
    const el = document.querySelector(`.rt-editor[data-path="${field.path}"]`) as HTMLElement;
    if (el) el.focus();
    document.execCommand('foreColor', false, color);
    this.editValues[field.path] = el ? el.innerHTML : this.editValues[field.path];
    this.markDirty(field);
    this.rtDropdown[field.path] = '';
  }

  rtStartLink(event: Event, field: any) {
    event.preventDefault();
    this.rtLinkInput[field.path] = { active: true, url: '' };
  }

  rtApplyLink(field: any) {
    const url = this.rtLinkInput[field.path]?.url;
    if (!url) return;
    const el = document.querySelector(`.rt-editor[data-path="${field.path}"]`) as HTMLElement;
    if (el) el.focus();
    document.execCommand('createLink', false, url);
    this.editValues[field.path] = el ? el.innerHTML : this.editValues[field.path];
    this.markDirty(field);
    this.rtLinkInput[field.path] = { active: false, url: '' };
  }

  rtCancelLink(field: any) {
    this.rtLinkInput[field.path] = { active: false, url: '' };
  }

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  // ─── Save ───────────────────────────────────────────────────

  async save() {
    if (this.saving) return;

    // Guard: require at least one dirty field
    if (this.dirtyPaths.size === 0) {
      this.errors = ['No fields have been changed.'];
      return;
    }

    // Threshold confirmation
    if (this.memberCount > BULK_CONFIRM_THRESHOLD) {
      const confirmed = await this.confirmBulkUpdate();
      if (!confirmed) return;
    }

    this.saving = true;
    this.errors = [];

    const changes: any = {};
    for (const field of this.view.fields) {
      if (!this.dirtyPaths.has(field.path)) continue;
      if (!field.editable || field.calculated === true) continue;
      if (field.locationSpecific && (!this.activeLocation || !this.activeLocation._id)) continue;

      let value = this.editValues[field.path];

      if (field.limit) {
        if (this.isDeltaField(field)) {
          const d = this.delta(field.path);
          changes[field.path] = { add: d.add.map(a => a.value), remove: d.remove };
          continue;
        }
        let arrValue = value || [];
        if (field.type === 'address' && Array.isArray(arrValue)) {
          arrValue = arrValue.filter(a => a.streetAddress || a.streetAddressExt
            || a.addressLocality || a.addressRegion || a.postalCode);
        }
        if (field.required && (!Array.isArray(arrValue) || arrValue.length === 0)) {
          this.errors.push(`${field.name} is required.`);
        }
        changes[field.path] = arrValue;
        continue;
      }

      if ((field.type === 'currency' || field.type === 'advancedPrice') && value != null && value !== '') {
        const num = Math.round(parseFloat(value) * 100);
        if (isNaN(num)) {
          this.errors.push(`${field.name} must be a valid number.`);
          this.saving = false;
          return;
        }
        value = num;
      } else if (field.type === 'dateTime') {
        const settings = this.dateTimeSettings[field.path];
        value = (settings && settings.dateRange.startDate) ? settings.dateRange.startDate.toISOString() : null;
      } else if (field.type === 'address' && !field.limit) {
        if (value && !value.streetAddress && !value.streetAddressExt
            && !value.addressLocality && !value.addressRegion && !value.postalCode) {
          value = null;
        }
      } else if (field.type === 'boolean') {
        // Coerce null to false if somehow still null when dirty
        if (value == null) value = false;
      }

      if (field.required && (value == null || value === '')) {
        this.errors.push(`${field.name} is required.`);
      }

      changes[field.path] = value;
    }

    if (this.errors.length > 0) {
      this.saving = false;
      return;
    }

    try {
      const result: any = await this.ioService.post('/view/updateBulkFromList', {
        viewKey: this.viewKey,
        listKey: this.listKey,
        changes,
        locationId: this.activeLocation?._id || null,
      });

      const skipped = result.skippedCount || 0;
      AppComponent.showSnack(
        `Updated ${result.modifiedCount} of ${result.matchedCount} items.`
          + (skipped ? ` ${skipped} skipped.` : ''),
        'OK', skipped ? 5000 : 4000
      );

      this.reportService.loadReport(null);
      // Refresh the active list in the side panel so the updated values show there too
      if (this.listBuilderService.getActiveList()?.key === this.listKey) {
        await this.listBuilderService.loadList(this.listKey);
      }

      if (skipped) {
        // Reload so the editor reflects the new state, and list who was skipped and why.
        await this.loadView();
        this.errors = (result.skipped || []).map((s: any) => `${s.name || s._id}: ${s.msg}`);
        this.saving = false;
      } else {
        this.close();
      }
    } catch (e) {
      this.errors = [e?.error?.msg || 'Failed to save bulk edit.'];
      this.saving = false;
    }
  }

  private confirmBulkUpdate(): Promise<boolean> {
    return new Promise((resolve) => {
      const dialogRef = AppComponent.showMessage(
        'Confirm Bulk Edit',
        `This will update ${this.memberCount} items in "${this.listName}". Continue?`,
        ['Confirm', 'Cancel'],
        false,
      );
      dialogRef.afterClosed().subscribe((result: any) => {
        resolve(!!result && result.value === 'Confirm');
      });
    });
  }

  private confirmBulkDelete(): Promise<boolean> {
    return new Promise((resolve) => {
      const dialogRef = AppComponent.showMessage(
        'Confirm Delete',
        `Permanently delete all ${this.memberCount} items in "${this.listName}"? `
          + 'Items that fail their delete checks are skipped and kept in the list.',
        ['Confirm', 'Cancel'],
        false,
      );
      dialogRef.afterClosed().subscribe((result: any) => {
        resolve(!!result && result.value === 'Confirm');
      });
    });
  }

  // Deletes every list member through the view's own per-item delete endpoint
  // (server-side: /view/deleteBulkFromList). Failures are skipped; the list
  // shrinks to just the failed members for review.
  async deleteAll() {
    if (this.deleting || this.saving) return;

    const confirmed = await this.confirmBulkDelete();
    if (!confirmed) return;

    this.deleting = true;
    this.errors = [];

    try {
      const result: any = await this.ioService.post('/view/deleteBulkFromList', {
        viewKey: this.viewKey,
        listKey: this.listKey,
      });

      const failed = result.failedCount || 0;
      AppComponent.showSnack(
        `Deleted ${result.deletedCount} items.`
          + (failed ? ` ${failed} failed and remain in the list.` : ''),
        'OK', 5000
      );

      this.reportService.loadReport(null);
      if (this.listBuilderService.getActiveList()?.key === this.listKey) {
        await this.listBuilderService.loadList(this.listKey);
      }

      if (failed) {
        // Reload so the editor now reflects only the remaining (failed) members.
        this.errors = (result.failures || []).map(
          (f: any) => `${f.name || f._id}: ${f.msg}`);
        await this.loadView();
      } else {
        this.close();
      }
    } catch (e) {
      this.errors = [e?.error?.msg || 'Bulk delete failed.'];
    } finally {
      this.deleting = false;
    }
  }
}
